import React, { useState, useMemo } from 'react'
import {
  Button,
  Divider,
  Heading,
  Typography,
  Frame,
  RadioGroup,
  TextField,
  SelectField
} from 'components'
import { Box, FormControlLabel, Radio, makeStyles } from '@material-ui/core'
import firebase from 'utils/firebase'
import { updateUser, useContent, getIndustriesFromMarkets } from 'utils'
import { ONBOARD_SKIP_VALIDATION } from '../../../constants'

const useStyles = makeStyles((theme) => ({
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  radioButton: {
    '&.MuiRadio-root': {
      padding: 12,
      transform: 'scale(1.5)',
    },
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '& .MuiIconButton-label:after': {
      content: '""',
      display: 'block',
      width: 18,
      height: 18,
      backgroundColor: 'white',
      border: '2px solid #999',
    },
    '&.Mui-checked .MuiIconButton-label:after': {
      backgroundColor: theme.palette.primary.main,
      border: '2px solid ' + theme.palette.primary.main,
    },
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '1.25rem',
      fontWeight: 500,
      marginLeft: theme.spacing(3), // Add space between button and label
    },
    margin: theme.spacing(2, 0),
  },
  optionBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 200,
  }
}))


const FreelancerSelectCrewOrPerformer = ({ user, markets, onNext, stepIndex, totalSteps, onboarding }) => {
  const classes = useStyles()
  const [selectedIndustry, setSelectedIndustry] = useState(user?.industryId || '')
  const [industryName, setIndustryName] = useState(user?.industry || '')
  const [performerStep, setPerformerStep] = useState(0)
  const [performerType, setPerformerType] = useState(user?.performerType || '')
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'

  // Get industries from markets instead of using the hook - memoize to prevent re-renders
  const industries = useMemo(() => {
    return markets ? getIndustriesFromMarkets(markets) : []
  }, [markets])

  // Appearance fields
  const [heightValue, setHeightValue] = useState(user?.heightValue || '')
  const [heightUnit, setHeightUnit] = useState(user?.heightUnit || 'Feet')
  const [heightInches, setHeightInches] = useState(user?.heightInches || '')
  const [weightValue, setWeightValue] = useState(user?.weightValue || '')
  const [weightUnit, setWeightUnit] = useState(user?.weightUnit || 'Pounds')
  const [hairColor, setHairColor] = useState(user?.hairColor || '')
  const [eyeColor, setEyeColor] = useState(user?.eyeColor || '')

  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry.id)
    setIndustryName(industry.label)
    // Reset performer step when changing industry
    setPerformerStep(0)
  }

  const handleNext = async () => {
    // Only show appearance step for Performer industry
    const isPerformerIndustry = industryName === 'Performers';

    // If performer industry and we're still collecting performer details
    if (isPerformerIndustry) {
      if (performerStep === 0) {
        // Move to performer type selection
        setPerformerStep(1)
        return
      }
      // else if (performerStep === 1) {
      //   // If no performer type selected, don't proceed
      //   // if (!performerType) return

      //   // Move to appearance details
      //   setPerformerStep(2)
      //   return
      // }
    }

    // Final step - save all data
    try {
      // Data to save
      const updateData = { industryId: selectedIndustry, industry: industryName }

      // If performer industry, save additional data
      if (isPerformerIndustry) {
        updateData.performerType = performerType

        // Only save appearance fields if they have values
        if (heightValue) updateData.heightValue = heightValue
        if (heightUnit) updateData.heightUnit = heightUnit
        if (heightUnit === 'Feet' && heightInches) updateData.heightInches = heightInches
        if (weightValue) updateData.weightValue = weightValue
        if (weightUnit) updateData.weightUnit = weightUnit
        if (hairColor) updateData.hairColor = hairColor
        if (eyeColor) updateData.eyeColor = eyeColor
      }

      // console.log('Updating user with industry data:', updateData);

      // Save to user document using direct Firestore for reliability
      await firebase
        .firestore()
        .collection('users')
        .doc(user.id)
        .set(updateData, { merge: true })

      // console.log('Industry data successfully saved to database');
      firebase.analytics().logEvent('user_industry_set', { industry: industryName })

      if (onNext) {
        onNext()
      }
    } catch (error) {
      console.error('Error updating role data:', error)
    }
  }

  // Initial industry selection screen
  if (performerStep === 0) {
    return (
      <Frame maxWidth="xs" user={user}>
        <Heading subheading={`Step ${stepIndex} of ${totalSteps}`}>
          Select your industry
        </Heading>

        <Box my={3} display="flex" justifyContent="center" width="100%">
          <Box className={classes.radioGroup}>
            {industries && industries.map(industry => (
              <Box key={industry.id} display="flex" justifyContent="center" width="100%">
                <Box className={classes.optionBox}>
                  <FormControlLabel
                    className={classes.formControlLabel}
                    value={industry.id}
                    control={<Radio
                      color="primary"
                      className={classes.radioButton}
                      checked={selectedIndustry === industry.id}
                      onChange={() => handleIndustryChange(industry)}
                    />}
                    label={industry.label}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography variant="body2" style={{ textAlign: 'center', marginBottom: 24 }}>
          Select the industry that best represents your work.
        </Typography>

        <Divider />

        <Box mx="auto" my={3} maxWidth={280}>
          <Button
            variant="contained"
            type="submit"
            style={{ width: '100%', 'background-color': buttonColor }}
            onClick={handleNext}
            disabled={!ONBOARD_SKIP_VALIDATION && !selectedIndustry}
          >
            Next
          </Button>
        </Box>
      </Frame>
    )
  }

  // Performer type selection screen
  if (performerStep === -1) {
    return (
      <Frame maxWidth="xs" user={user}>
        <Heading>
          What kind of Performer are you?
        </Heading>

        <Box my={3} display="flex" justifyContent="center" width="100%">
          <Box className={classes.radioGroup}>
            {['Actor', 'Dancer', 'Singer', 'Musician', 'Entertainer'].map(type => (
              <Box key={type} display="flex" justifyContent="center" width="100%">
                <Box className={classes.optionBox}>
                  <FormControlLabel
                    className={classes.formControlLabel}
                    value={type}
                    control={<Radio
                      color="primary"
                      className={classes.radioButton}
                      checked={performerType === type}
                      onChange={() => setPerformerType(type)}
                    />}
                    label={type}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography style={{ marginBottom: 24 }}>
          If you work as more than one, the other Role can be added later from your Profile page.
        </Typography>

        <Divider />

        <Box mx="auto" my={3} maxWidth={280}>
          <Button
            variant="contained"
            type="submit"
            style={{ width: '100%', 'background-color': buttonColor }}
            onClick={handleNext}
            disabled={!ONBOARD_SKIP_VALIDATION && !performerType}
          >
            Next
          </Button>
        </Box>
      </Frame>
    )
  }

  // Performer appearance details screen
  if (performerStep === 1) {
    const isValid = ONBOARD_SKIP_VALIDATION || heightValue || weightValue || hairColor || eyeColor

    return (
      <Frame maxWidth="xs" user={user}>
        <Heading>
          Please give us details about your appearance.
        </Heading>

        <Box display="flex" flexDirection="column" alignItems="center" width="100%" maxWidth={480} mx="auto">
          {/* Height Row */}
          <Box my={2} display="flex" alignItems="center" width="100%">
            <Box width="120px" textAlign="right" pr={2}>
              <Typography variant="subtitle1">
                Height
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" flexGrow={1}>
              <Box flexGrow={1} mr={2}>
                <TextField
                  type="number"
                  value={heightValue}
                  onChange={(e) => setHeightValue(e.target.value)}
                  min={0}
                  style={{ width: '100%' }}
                />
              </Box>
              <Box minWidth={100}>
                <SelectField
                  value={heightUnit}
                  onChange={(e) => setHeightUnit(e.target.value)}
                  choices={[
                    { value: 'Feet', label: 'Feet' },
                    { value: 'cm', label: 'cm' }
                  ]}
                />
              </Box>
              {heightUnit === 'Feet' && (
                <Box ml={2} minWidth={100}>
                  <TextField
                    label="Inches"
                    type="number"
                    value={heightInches}
                    onChange={(e) => setHeightInches(e.target.value)}
                    min={0}
                    max={11}
                    style={{ width: '100%' }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Weight Row */}
          <Box my={2} display="flex" alignItems="center" width="100%">
            <Box width="120px" textAlign="right" pr={2}>
              <Typography variant="subtitle1">
                Weight
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" flexGrow={1}>
              <Box flexGrow={1} mr={2}>
                <TextField
                  type="number"
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                  min={0}
                  style={{ width: '100%' }}
                />
              </Box>
              <Box minWidth={100}>
                <SelectField
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value)}
                  choices={[
                    { value: 'Pounds', label: 'Pounds' },
                    { value: 'Kg', label: 'Kg' }
                  ]}
                />
              </Box>
            </Box>
          </Box>

          {/* Hair Color Row */}
          <Box my={2} display="flex" alignItems="center" width="100%">
            <Box width="120px" textAlign="right" pr={2}>
              <Typography variant="subtitle1">
                Hair Colour
              </Typography>
            </Box>
            <Box flexGrow={1}>
              <TextField
                value={hairColor}
                onChange={(e) => setHairColor(e.target.value)}
                style={{ width: '100%' }}
              />
            </Box>
          </Box>

          {/* Eye Color Row */}
          <Box my={2} display="flex" alignItems="center" width="100%">
            <Box width="120px" textAlign="right" pr={2}>
              <Typography variant="subtitle1">
                Eye Colour
              </Typography>
            </Box>
            <Box flexGrow={1}>
              <TextField
                value={eyeColor}
                onChange={(e) => setEyeColor(e.target.value)}
                style={{ width: '100%' }}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box mx="auto" my={3} maxWidth={280}>
          <Button
            variant="contained"
            type="submit"
            style={{ width: '100%', 'background-color': buttonColor }}
            onClick={handleNext}
            disabled={!isValid}
          >
            Next
          </Button>
        </Box>
      </Frame>
    )
  }
}

export default FreelancerSelectCrewOrPerformer
